import { Component } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const wrapperClass = this.props.fullPage
      ? 'min-h-screen flex items-center justify-center bg-light-bg-primary dark:bg-dark-bg-primary'
      : 'w-full flex-1 flex items-center justify-center'

    return (
      <div className={wrapperClass}>
        <div className="max-w-lg w-full text-center space-y-10">

          {/* Illustration */}
          <div className="relative select-none">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 bg-amber-500/10 dark:bg-amber-400/5 rounded-full blur-3xl" />
            </div>
            <div className="relative">
              <h1
                className="text-[10rem] leading-none font-black tracking-tighter text-light-bg-tertiary dark:text-dark-bg-tertiary"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                500
              </h1>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-20 w-20 bg-gradient-to-br from-amber-500 to-orange-500 dark:from-amber-400 dark:to-orange-400 rounded-2xl rotate-12 flex items-center justify-center shadow-xl shadow-amber-500/20">
                  <AlertTriangle className="h-10 w-10 text-white -rotate-12" />
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
              Something went wrong
            </h2>
            <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary max-w-xs mx-auto leading-relaxed">
              An unexpected error occurred. You can try reloading the page or go back to the home screen.
            </p>
            {this.state.error && (
              <p className="text-xs text-light-text-tertiary/60 dark:text-dark-text-tertiary/60 font-mono mt-2 max-w-xs mx-auto break-words">
                {this.state.error.message}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-xs mx-auto">
            <button
              onClick={this.handleReset}
              className="flex-1 inline-flex items-center justify-center px-5 py-3 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-650 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-md shadow-primary-600/20"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </button>
            <a
              href="/"
              className="flex-1 inline-flex items-center justify-center px-5 py-3 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-lg text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </a>
          </div>

          {/* Footer note */}
          <p className="text-xs text-light-text-tertiary/60 dark:text-dark-text-tertiary/60">
            Error 500 &middot; Internal Error
          </p>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
